import type { ReactNode } from "react"
import {
	IconFolderOutline18 as Folder,
	IconRepeatOutline18 as RepeatIcon,
} from "nucleo-ui-outline-18"

import { Alert, Badge, Button } from "@productlane/ui"

import { isDevEnvironment } from "@/env"
import { NotFound } from "@/screens/not-found"
import { SettingItem } from "@/screens/settings/settings-switches/setting-item"
import {
	SETTINGS_INSET_CLASS,
	SettingsContainer,
} from "@/screens/settings/settings-switches/settings-container"

const REPO = "productlane/productlane"

interface BannerProps {
	repo: string
	onMap: () => void
}

// 1. The PaymentIssueAlert shape: amber alert, body, button under the text.
export function MapBannerPaymentShape({ repo, onMap }: BannerProps) {
	return (
		<Alert
			variant="warning"
			header="Your product is not mapped yet"
			className={SETTINGS_INSET_CLASS}
		>
			<div className="flex flex-col items-start gap-3">
				<span>
					The agent answers from agent-only articles, and {repo} has none yet.
					Map your product to write one for every screen your customers reach.
				</span>
				<Button variant="outline" Icon={RepeatIcon} onClick={onMap}>
					Map now
				</Button>
			</div>
		</Alert>
	)
}

// 2. Same alert, action pinned right so the banner stays two lines tall.
export function MapBannerInlineAction({ repo, onMap }: BannerProps) {
	return (
		<Alert
			variant="warning"
			header="Your product is not mapped yet"
			className={SETTINGS_INSET_CLASS}
		>
			<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
				<span>
					{repo} is linked. Map your product so the agent knows every screen
					your customers reach.
				</span>
				<Button
					variant="outline"
					Icon={RepeatIcon}
					className="flex-none"
					onClick={onMap}
				>
					Map now
				</Button>
			</div>
		</Alert>
	)
}

// 3. Blue guidance instead of amber: a step left, rather than something wrong.
export function MapBannerInfo({ repo, onMap }: BannerProps) {
	return (
		<Alert
			variant="info"
			header="One step left: map your product"
			className={SETTINGS_INSET_CLASS}
		>
			<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
				<span>
					Productlane reads {repo} once and writes an agent-only article for
					every screen your customers reach.
				</span>
				<Button
					variant="outline"
					Icon={RepeatIcon}
					className="flex-none"
					onClick={onMap}
				>
					Map now
				</Button>
			</div>
		</Alert>
	)
}

// 4. Amber alert carrying a step count, so the banner says where setup stands.
export function MapBannerWithProgress({ repo, onMap }: BannerProps) {
	return (
		<Alert
			variant="warning"
			icon={RepeatIcon}
			header={
				<div className="flex items-center gap-2">
					<span>Your product is not mapped yet</span>
					<Badge size="sm" variant="transparent" className="!text-current">
						Step 3 of 3
					</Badge>
				</div>
			}
			className={SETTINGS_INSET_CLASS}
		>
			<div className="flex flex-col items-start gap-3">
				<span>
					The GitHub App is installed and {repo} is linked. The agent starts
					using your screens once the mapping runs.
				</span>
				<div className="flex items-center gap-2">
					<Button variant="outline" Icon={RepeatIcon} onClick={onMap}>
						Map now
					</Button>
					<Button variant="ghost" className="!text-current">
						How it works
					</Button>
				</div>
			</div>
		</Alert>
	)
}

// 5. Loud amber bar for the top of the page, above the integration header.
export function MapBannerPageTop({ repo, onMap }: BannerProps) {
	return (
		<div className="flex items-center justify-between gap-4 rounded-lg bg-yellow-100 px-4 py-3 text-sm text-yellow-800 ring-1 ring-inset ring-yellow-600/20 dark:bg-yellow-400/[0.15] dark:text-yellow-300 dark:ring-yellow-400/25">
			<div className="flex items-center gap-3">
				<span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-yellow-200 text-yellow-900 dark:bg-yellow-400/20 dark:text-yellow-200 [&_svg]:h-4 [&_svg]:w-4">
					<RepeatIcon />
				</span>
				<div className="flex flex-col gap-0.5">
					<span className="font-semibold">Your product is not mapped yet</span>
					<span className="text-xs opacity-90">
						{repo} is linked. The agent answers from your screens once the
						mapping runs.
					</span>
				</div>
			</div>
			<Button variant="outline" Icon={RepeatIcon} onClick={onMap}>
				Map now
			</Button>
		</div>
	)
}

function RepositoryBox() {
	return (
		<SettingsContainer>
			<SettingItem
				Icon={Folder}
				title="Linked repository"
				description="The one repository the Productlane Agent reads from."
				action={
					<Button variant="outline" className="w-36">
						{REPO}
					</Button>
				}
			/>
			<SettingItem
				Icon={RepeatIcon}
				title="Map product for the agent"
				description="Writes an agent-only article for every screen in the repo."
				action={
					<Button variant="outline" Icon={RepeatIcon}>
						Map now
					</Button>
				}
			/>
		</SettingsContainer>
	)
}

export function RepoMapCalloutPage() {
	if (!isDevEnvironment) return <NotFound />

	const onMap = () => {
		console.log("Map now")
	}

	return (
		<div className="h-full overflow-y-auto bg-gray-50 dark:bg-gray-900">
			<div className="mx-auto max-w-[930px] px-6 py-10 pb-24">
				<div className="mb-8">
					<div className="text-lg font-semibold text-primary">
						Map product banner, five options
					</div>
					<div className="mt-1 text-sm text-secondary">
						The state where a repo is linked and the mapping has never run.
					</div>
				</div>
				<div className="space-y-12">
					<Variant
						label="1. Payment alert shape"
						note="The PaymentIssueAlert layout, unchanged: amber, header, body, button under the text."
					>
						<MapBannerPaymentShape repo={REPO} onMap={onMap} />
					</Variant>
					<Variant
						label="2. Amber, action right"
						note="Same alert, button pinned right so the banner stays two lines tall."
					>
						<MapBannerInlineAction repo={REPO} onMap={onMap} />
					</Variant>
					<Variant
						label="3. Blue guidance"
						note="Reads as a step left rather than something broken. Quieter than amber."
					>
						<MapBannerInfo repo={REPO} onMap={onMap} />
					</Variant>
					<Variant
						label="4. Amber with a step count"
						note="Carries a Step 3 of 3 badge and a second link, so the banner says where setup stands."
					>
						<MapBannerWithProgress repo={REPO} onMap={onMap} />
					</Variant>
					<Variant
						label="5. Loud page-top bar"
						note="Heavier amber, icon in a halo, sits above the integration header rather than the card."
					>
						<MapBannerPageTop repo={REPO} onMap={onMap} />
					</Variant>
				</div>
			</div>
		</div>
	)
}

function Variant({
	label,
	note,
	children,
}: {
	label: string
	note: string
	children: ReactNode
}) {
	return (
		<div className="space-y-2">
			<div className="text-xs font-medium text-primary">{label}</div>
			<div className="text-xxs text-tertiary">{note}</div>
			<div className="space-y-4 pt-2">
				{children}
				<div className="text-sm font-medium">Repository</div>
				<RepositoryBox />
			</div>
		</div>
	)
}
